import { useEffect, useState } from "react";

import {
  Box,
  Button,
  Drawer,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";

import { userState } from "../../../interface/MainInterface";
import { HttpAdminApi } from "../../../interface/admin-api";
import { useWindowSize } from "../../../utils/useWindowSize";
import LoadingCircle from "../../../utils/LoadingCircle";
import S3UploaderViaApi from "../../../utils/attach/S3UploaderViaApi";

interface PropsType {
  userState: userState;
  channelInfo: any;
  callback: any;
}

type BannerAdLinkType = "NORMAL_URL" | "DEEPLINK";

const adminApi = new HttpAdminApi();
const DEFAULT_BANNER_AD_LINK_TYPE: BannerAdLinkType = "NORMAL_URL";
const BANNER_AD_LINK_TYPES: BannerAdLinkType[] = ["NORMAL_URL", "DEEPLINK"];
const THE_HYUNDAI_HI_HOST = "hi-dev.thehyundai.com";

const normalizeBannerAdLinkType = (linkType: any): BannerAdLinkType => {
  return BANNER_AD_LINK_TYPES.includes(linkType) ? linkType : DEFAULT_BANNER_AD_LINK_TYPE;
};

const isTheHyundaiHiUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname === THE_HYUNDAI_HI_HOST;
  } catch {
    return false;
  }
};

const BannerAdManagement = (props: PropsType) => {
  const windowSize = useWindowSize();

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bannerAdUseYn, setBannerAdUseYn] = useState("N");
  const [bannerAdImageUrl, setBannerAdImageUrl] = useState("");
  const [bannerAdLinkUrl, setBannerAdLinkUrl] = useState("");
  const [bannerAdLinkType, setBannerAdLinkType] = useState<BannerAdLinkType>(DEFAULT_BANNER_AD_LINK_TYPE);

  useEffect(() => {
    if (open) return;
    setBannerAdUseYn(props.channelInfo?.banner_ad_use_yn || "N");
    setBannerAdImageUrl(props.channelInfo?.banner_ad_image_url || "");
    setBannerAdLinkUrl(props.channelInfo?.banner_ad_link_url || "");
    setBannerAdLinkType(normalizeBannerAdLinkType(props.channelInfo?.banner_ad_link_type));
  }, [open, props.channelInfo]);

  const handleUploadComplete = (result: any) => {
    if (result.status === "ok") {
      setBannerAdImageUrl(result.fileInfo[0]?.FILE_KEY || "");
      return;
    }

    alert(result.msg || "이미지 업로드에 실패했습니다.");
  };

  const validateBannerAdContent = (
    nextBannerAdImageUrl: string,
    nextBannerAdLinkUrl: string,
    nextBannerAdLinkType = bannerAdLinkType,
    allowEmptyContent = false
  ) => {
    const nextBannerAdLinkUrlTrimmed = nextBannerAdLinkUrl.trim();

    if (allowEmptyContent && nextBannerAdImageUrl === "" && nextBannerAdLinkUrlTrimmed === "") {
      return true;
    }

    if (nextBannerAdImageUrl === "") {
      alert("배너 광고 이미지를 업로드해주세요.");
      return false;
    }

    if (nextBannerAdLinkUrlTrimmed === "") {
      alert("배너 광고 링크 URL을 입력해주세요.");
      return false;
    }

    if (nextBannerAdLinkType === "DEEPLINK" && !isTheHyundaiHiUrl(nextBannerAdLinkUrlTrimmed)) {
      alert("딥링크 URL은 https://hi-dev.thehyundai.com 도메인을 사용해주세요.");
      return false;
    }

    return true;
  };

  const updateBannerAd = async (
    nextUseYn: string,
    closeOnSuccess: boolean,
    nextBannerAdImageUrl = bannerAdImageUrl,
    nextBannerAdLinkUrl = bannerAdLinkUrl,
    nextBannerAdLinkType = bannerAdLinkType
  ) => {
    const nextBannerAdLinkUrlTrimmed = nextBannerAdLinkUrl.trim();
    const nextBannerAdLinkTypeNormalized = normalizeBannerAdLinkType(nextBannerAdLinkType);

    if (
      nextUseYn === "Y" &&
      !validateBannerAdContent(nextBannerAdImageUrl, nextBannerAdLinkUrlTrimmed, nextBannerAdLinkTypeNormalized)
    ) {
      return false;
    }

    setIsLoading(true);
    const param = {
      command: "update_banner_ad",
      channel_pk: props.channelInfo.channel_pk || props.channelInfo.pk,
      broad_seq: props.channelInfo.broad_seq,
      banner_ad_use_yn: nextUseYn,
      banner_ad_image_url: nextBannerAdImageUrl,
      banner_ad_link_url: nextBannerAdLinkUrlTrimmed,
      banner_ad_link_type: nextBannerAdLinkTypeNormalized,
    };

    const res = await adminApi.post(param);
    setIsLoading(false);

    if (res.code === "200") {
      setBannerAdUseYn(nextUseYn);
      setBannerAdImageUrl(nextBannerAdImageUrl);
      setBannerAdLinkUrl(nextBannerAdLinkUrlTrimmed);
      setBannerAdLinkType(nextBannerAdLinkTypeNormalized);
      props.callback({ command: "update_banner_ad" });
      if (closeOnSuccess) setOpen(false);
      return true;
    } else {
      alert(res.response?.error_msg || "배너 광고 설정 저장에 실패했습니다.");
      return false;
    }
  };

  const handleBannerAdUseChange = async (checked: boolean) => {
    const previousUseYn = bannerAdUseYn;
    const nextUseYn = checked ? "Y" : "N";

    setBannerAdUseYn(nextUseYn);
    const success = await updateBannerAd(nextUseYn, false);
    if (!success) setBannerAdUseYn(previousUseYn);
  };

  const saveBannerAd = async () => {
    if (!validateBannerAdContent(bannerAdImageUrl, bannerAdLinkUrl, bannerAdLinkType, true)) return;
    if (!window.confirm("배너 광고 설정을 저장하시겠습니까?")) return;

    const nextBannerAdUseYn = bannerAdImageUrl === "" && bannerAdLinkUrl.trim() === "" ? "N" : bannerAdUseYn;
    await updateBannerAd(nextBannerAdUseYn, false);
  };

  return (
    <>
      <Box sx={{ cursor: "pointer", minWidth: "70px" }} onClick={() => setOpen(true)}>
        <Stack
          direction="column"
          spacing={0}
          sx={{ backgroundColor: "#1876d2", color: "#ffffff", borderRadius: "7px", p: 1 }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <CampaignIcon />
          </Box>
          <Typography sx={{ fontSize: "14px", textAlign: "center", whiteSpace: "pre-line" }}>
            {"배너광고"}
          </Typography>
        </Stack>
      </Box>

      <Drawer
        anchor={windowSize.width > 768 ? "right" : "top"}
        open={open}
        onClose={() => setOpen(false)}
      >
        <Box sx={{ width: windowSize.width > 768 ? "500px" : "100vw" }}>
          <Box sx={{ p: 2 }}>
            <Stack direction="column" spacing={2}>
              <Typography sx={{ fontSize: "18px", fontWeight: "700" }}>배너 광고 관리</Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={bannerAdUseYn === "Y"}
                    onChange={(event) => handleBannerAdUseChange(event.target.checked)}
                    disabled={isLoading}
                  />
                }
                label={bannerAdUseYn === "Y" ? "배너 광고 On" : "배너 광고 Off"}
              />

              <TextField
                select
                label="링크 타입"
                fullWidth
                size="small"
                value={bannerAdLinkType}
                onChange={(event) => setBannerAdLinkType(normalizeBannerAdLinkType(event.target.value))}
              >
                <MenuItem value="NORMAL_URL">일반 URL</MenuItem>
                <MenuItem value="DEEPLINK">딥링크</MenuItem>
              </TextField>

              <TextField
                label={bannerAdLinkType === "DEEPLINK" ? "딥링크 URL" : "일반 URL"}
                placeholder={bannerAdLinkType === "DEEPLINK" ? "딥링크 URL을 입력해주세요." : "https://www.example.com"}
                fullWidth
                size="small"
                value={bannerAdLinkUrl}
                onChange={(event) => setBannerAdLinkUrl(event.target.value)}
                helperText={bannerAdLinkType === "DEEPLINK" ? "딥링크는 더현대Hi URL을 사용해주세요." : ""}
                FormHelperTextProps={{
                  sx: { color: "#d32f2f" },
                }}
              />

              {bannerAdImageUrl !== "" && (
                <Box
                  component="img"
                  src={bannerAdImageUrl}
                  alt="배너 광고 이미지"
                  sx={{
                    width: "100%",
                    maxHeight: "180px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "7px",
                    objectFit: "contain",
                    backgroundColor: "#f7f7f7",
                  }}
                />
              )}

              <Box sx={{ p: 1.5, backgroundColor: "#f7f7f7", borderRadius: "7px" }}>
                <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>업로드 이미지 권장 사이즈</Typography>
                <Typography sx={{ fontSize: "12px", color: "#666666", mt: 0.5 }}>
                  권장 비율: 320x50
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#666666", mt: 0.5 }}>
                  사용 가능 사이즈: 640x100 이상
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#666666", mt: 0.5 }}>
                  권장 사이즈: 960x150 이상
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#666666", mt: 0.5 }}>
                  텍스트와 로고는 좌우 가장자리에서 여유 있게 배치해주세요.
                </Typography>
              </Box>

              <S3UploaderViaApi
                userState={props.userState}
                multiple={false}
                callBack={handleUploadComplete}
                setIsLoading={setIsLoading}
                borderRadius=""
                width="100%"
                variant="contained"
                buttonText={bannerAdImageUrl === "" ? "이미지 선택" : "이미지 교체"}
                dragDrop={true}
                uploadDir="banner-ad-image"
              />

              <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
                <Stack direction="row" spacing={2}>
                  {bannerAdImageUrl !== "" && (
                    <Button variant="outlined" color="error" onClick={() => setBannerAdImageUrl("")}>
                      이미지 삭제
                    </Button>
                  )}
                  <Button variant="contained" onClick={saveBannerAd}>
                    저장
                  </Button>
                  <Button variant="outlined" onClick={() => setOpen(false)}>
                    닫기
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Drawer>
      <LoadingCircle loading={isLoading} />
    </>
  );
};

export default BannerAdManagement;
